import { useLayoutEffect, type RefObject } from 'react';
import { autoPlacement, computePosition, flip, offset, platform, shift } from '@floating-ui/react';
import { animate } from 'framer-motion';
import type { StepMerged } from 'react-joyride';
import { measureTourSpotlight, resolveTourTarget, tourSpotlightPath, type ITourRect } from './tourGeometry';

interface ITourGeometryOptions {
    step?: StepMerged;
    mounted: boolean;
    closing: boolean;
    reducedMotion: boolean;
    cardRef: RefObject<HTMLDivElement | null>;
    coverRef: RefObject<SVGPathElement | null>;
    outlineRef: RefObject<SVGPathElement | null>;
    geometryRef: RefObject<ITourRect | null>;
    onPositioned: () => void;
}

export function useTourGeometry({
    step,
    mounted,
    closing,
    reducedMotion,
    cardRef,
    coverRef,
    outlineRef,
    geometryRef,
    onPositioned,
}: ITourGeometryOptions) {
    useLayoutEffect(() => {
        const card = cardRef.current;
        if (!step || !mounted || closing || !card) return;
        let frame = 0;
        let lastCheck = -Infinity;
        let previous = '';
        let generation = 0;
        let disposed = false;
        let animation: ReturnType<typeof animate> | undefined;

        const draw = (rect: ITourRect | null) => {
            geometryRef.current = rect;
            const hole = rect ? tourSpotlightPath(rect) : '';
            coverRef.current?.setAttribute('d', `M0,0H${window.innerWidth}V${window.innerHeight}H0Z${hole}`);
            outlineRef.current?.setAttribute('d', hole);
        };

        const update = () => {
            const target = resolveTourTarget(step.target);
            if (!target) return;
            const rect = measureTourSpotlight(step);
            const targetRect = target.getBoundingClientRect();
            const inner = card.firstElementChild as HTMLElement | null;
            const height = (inner?.getBoundingClientRect().height ?? 0) + card.clientTop * 2;
            const width = Math.min(384, window.innerWidth - 24);
            const signature = JSON.stringify([
                rect,
                targetRect.x,
                targetRect.y,
                targetRect.width,
                targetRect.height,
                width,
                height,
                window.innerHeight,
            ]);
            if (signature === previous || !height) return;
            previous = signature;
            const ownGeneration = ++generation;
            card.style.width = `${width}px`;
            card.style.height = `${height}px`;

            const from = geometryRef.current;
            animation?.stop();
            if (from && rect && !reducedMotion) {
                animation = animate(0, 1, {
                    duration: 0.38,
                    ease: [0.32, 0.72, 0.28, 1],
                    onUpdate: (progress) =>
                        draw({
                            left: from.left + (rect.left - from.left) * progress,
                            top: from.top + (rect.top - from.top) * progress,
                            width: from.width + (rect.width - from.width) * progress,
                            height: from.height + (rect.height - from.height) * progress,
                            radius: from.radius + (rect.radius - from.radius) * progress,
                        }),
                });
            } else draw(rect);

            if (step.placement === 'center') {
                card.style.left = `${Math.max(12, (window.innerWidth - width) / 2)}px`;
                card.style.top = `${Math.max(12, (window.innerHeight - height) / 2)}px`;
                onPositioned();
                return;
            }
            void computePosition(target, card, {
                strategy: 'fixed',
                placement: step.placement === 'auto' ? 'bottom' : step.placement,
                platform: {
                    ...platform,
                    // The shell is still transitioning; position against its destination dimensions.
                    getDimensions: (element) =>
                        element === card ? { width, height } : platform.getDimensions(element),
                },
                middleware: [
                    offset(20),
                    step.placement === 'auto' ? autoPlacement({ padding: 16 }) : flip({ padding: 16 }),
                    shift({ padding: 12 }),
                ],
            }).then(({ x, y }) => {
                if (disposed || ownGeneration !== generation) return;
                card.style.left = `${Math.max(12, Math.min(x, window.innerWidth - width - 12))}px`;
                card.style.top = `${Math.max(12, Math.min(y, window.innerHeight - height - 12))}px`;
                onPositioned();
            });
        };

        const tick = (timestamp: number) => {
            frame = requestAnimationFrame(tick);
            if (timestamp - lastCheck < 100) return;
            lastCheck = timestamp;
            update();
        };
        update();
        frame = requestAnimationFrame(tick);
        const observer = new ResizeObserver(update);
        if (card.firstElementChild) observer.observe(card.firstElementChild);
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            disposed = true;
            cancelAnimationFrame(frame);
            animation?.stop();
            observer.disconnect();
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [step, mounted, closing, reducedMotion, cardRef, coverRef, outlineRef, geometryRef, onPositioned]);
}
